import { useEffect, useState }from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ArchiveBoxArrowDownIcon, ArchiveBoxXMarkIcon, TrashIcon } from '@heroicons/react/24/outline'
import { archiveBien, restoreBien, deleteBien } from './biensSlice' 
import { EmptyValuePlaceholder } from "@/components/ui"
import { SingleLineAddressOnListing } from '@/components/Address'
import { capitalize } from '@/utils/strings'
import { formatDate, formatPrice, formatSurface} from '@/utils/formatters'

const BienItem = ({ bien, contactContext = false }) => {
  const dispatch = useDispatch()
  const archiveLoading = useSelector((state) => state.biens.loadingStatus.archive)
  const restoreLoading = useSelector((state) => state.biens.loadingStatus.restore)
  const requestError = useSelector((state) => state.biens.requestStatus.error)
  const [animating, setAnimating] = useState(false)
  const [pendingAction, setPendingAction] = useState(false)
  const [ showConfirmDialog, setShowConfirmDialog ] = useState(false)

  useEffect(() => {
    if (!archiveLoading && !restoreLoading && pendingAction) {
      if(!requestError) {
        setTimeout(() => {
          setAnimating(false)
          setPendingAction(false)
        }, 300) // Durée de l'animation
      } else {
        setAnimating(false)
        setPendingAction(false)
      }
    }
  }, [archiveLoading, restoreLoading])

  const handleRowClick = (bienId) => {
    window.location.href = `/biens/${bienId}` // Rediriger vers la page de détails du bien cliqué
  }

  const handleArchiveClick = (bienId) => {
    // Ajouter l'ID du contact à la liste des contacts en cours d'animation
    setAnimating(true)
    // Ajouter l'ID du contact à la liste des actions en attente
    setPendingAction(true)
    
    // Dispatcher l'action immédiatement
    dispatch(!!bien.archived_at ? 
      restoreBien(bienId) :
      archiveBien(bienId)
    )
    // La fin de l'animation sera gérée par l'effet ci-dessus
  }

  const handleDeleteClick = () => {
    setShowConfirmDialog(true)
  }
  const confirmDelete = () => {
    dispatch(deleteBien(bien.id))
    setShowConfirmDialog(false)
  }

  const cancelDelete = () => {
    setShowConfirmDialog(false)
  }

  return (<>
    <tr onClick={() => handleRowClick(bien.id)}
        data-object="bien" data-id={bien.id}
        className={`cursor-pointer text-sm hover:bg-primary-200 transition-all duration-500 ${
            animating ? 'opacity-50 transform scale-98 translate-x-2' 
            : 'opacity-100 transform scale-100'
        }`}
    >
      <td className="p-1">
        <div className={`flex items-center ${bien.archived_at && 'tooltip tooltip-right'}`}>
          {bien.archived_at && (
            <div className='tooltip-content'>
              archivé le {formatDate(bien.archived_at)}
            </div>
          )}
          <EmptyValuePlaceholder value={bien.codage_interne} />
          {bien.archived_at && (
            <div className="badge-container ml-2">
              <span className="badge badge-ghost px-3 py-3 text-xs">
                archivé
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="p-1">
        <div className={`flex items-center ${bien.archived_at && 'tooltip tooltip-right'}`}>          
          { bien.mandats.length < 2 && <EmptyValuePlaceholder value={bien.mandats[0]?.type_mandat} className='capitalize'/>  }
          { bien.mandats.length > 1 && 
              <div className='flex flex-col'>{
                bien.mandats.map((mandat, i) => <div key={i} className="capitalize">{mandat.type_mandat}</div>)
              }</div>
          }
        </div>
      </td>
      <td className="p-1">
        { bien.mandats.length < 2 && <EmptyValuePlaceholder value={bien.mandats[0]?.numero}/>  }
        { bien.mandats.length > 1 && (
            <div className='flex flex-col'>
              {
                bien.mandats.map((mandat, i) => (
                  <div key={i} className={`${mandat.actif ? "" : "italic"}`}>
                    {mandat.numero}
                    {!mandat.actif && <span className="ml-2">fin le {formatDate(mandat.date_fin)}</span>}
                  </div>
                )
                )
              }
            </div>
        )}
      </td>
      <td className="p-1"><EmptyValuePlaceholder value={formatPrice(bien.prix_vente || bien.prix_loyer)} /></td>
      <td className="p-1">
        <SingleLineAddressOnListing data={bien} />
      </td>
      <td className="p-1"><EmptyValuePlaceholder value={capitalize(bien.type_bien)} /></td>
      <td className="p-1"><EmptyValuePlaceholder value={formatSurface(bien.surface_carrez)} /></td>
      <td className="p-1">{ bien.proprietaires?.map((prop, i) => <div key={i} className="uppercase">{prop.nom_complet}</div>) }
      </td>
      <td className="p-1">{ Array.isArray(bien.locataires) 
        ? bien.locataires.map((loc, i) => <div key={i} className="uppercase">{loc.nom_complet}</div>) 
        : ""}
      </td>
      { contactContext && <td></td>}
      <td className="flex p-1 gap-2">
        <button 
          id='archiveBtn' 
          className={`btn btn-sm btn-ghost btn-square hover:bg-primary-400 ${
            pendingAction ? 'loading' : ''
          }`} 
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!pendingAction) {
              handleArchiveClick(bien.id)
            }
          }}
          disabled={pendingAction}
          title={`${!!bien.archived_at ? "Restaurer" : "Archiver"} le bien`}
        >
          {!pendingAction && (
            !!bien.archived_at ? <ArchiveBoxXMarkIcon className="h-5 w-5" /> : <ArchiveBoxArrowDownIcon className="h-5 w-5" />
          )}
        </button>
        {!pendingAction && !contactContext && !!bien.archived_at && (
          <button 
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDeleteClick()
            }} 
            title="Supprimer le bien"
            className="p-1 btn btn-sm btn-ghost btn-square hover:bg-primary-400">
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </td>
    </tr>
    {showConfirmDialog && (
      <dialog open className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Confirmer la suppression</h3>
          <p className="py-4">Êtes-vous sûr de vouloir supprimer ce bien ?</p>
          <div className="modal-action">
            <button onClick={confirmDelete} className="btn btn-secondary">Supprimer</button>
            <button onClick={cancelDelete} className="btn btn-outline">Annuler</button>
          </div>
        </div>
      </dialog>
    )}
  </>
  )
}

export default BienItem